import React, { useMemo } from 'react'
import { View } from 'react-native'
import moment from 'moment'
import { Icon, Text } from '@src/components/base'
import { useIsDarkMode } from '@src/config/theme'
import { StyleService, useStyleSheet } from '@src/style/service'
import { ActionGoalCompletion } from '@src/types'
import { WEEKDAY_ABBREVIATIONS } from '../constants'

type DayCompletionData = {
  show: boolean
  displayString: string
}

interface ActionGoalCompletionMarkersProps {
  completions: ActionGoalCompletion[]
}

export const ActionGoalCompletionMarkers = ({ completions }: ActionGoalCompletionMarkersProps) => {
  const styles = useStyleSheet(themedStyles)
  const isDarkMode = useIsDarkMode()
  // Create a mapping of days of the week to completion status
  const datesToStatus = useMemo(() => {
    const presentDayAbbrSet = new Set<string>()
    completions
      .map((completion) => moment(completion.date).format('ddd'))
      .forEach((dayAbbr) => presentDayAbbrSet.add(dayAbbr))

    const weekPresenceMap: Record<string, DayCompletionData> = {}

    WEEKDAY_ABBREVIATIONS.forEach((dayAbbr) => {
      weekPresenceMap[dayAbbr] = {
        show: presentDayAbbrSet.has(dayAbbr),
        displayString: dayAbbr.charAt(0), // Use first letter for display
      }
    })

    return weekPresenceMap
  }, [completions])

  const notCompletedIconStyle = isDarkMode ? styles.notCompletedIconDark : styles.notCompletedIcon

  return Object.entries(datesToStatus).map(([dayAbbr, completion]) => (
    <View style={styles.dayCompletionContainer} key={dayAbbr}>
      <Text type="tiny" style={styles.dayAbbreviationText}>
        {completion.displayString}
      </Text>
      {completion.show ? (
        <Icon name="check-circle" weight="fill" style={styles.completionIcon} />
      ) : (
        <Icon name="circle" weight="fill" style={notCompletedIconStyle} />
      )}
    </View>
  ))
}

const themedStyles = StyleService.create({
  dayCompletionContainer: {
    marginRight: 12,
    justifyContent: 'center',
    flexDirection: 'column',
  },
  dayAbbreviationText: {
    textAlign: 'center',
    color: 'theme.text.tertiary',
  },
  completionIcon: {
    color: 'theme.success.base',
  },
  notCompletedIcon: {
    color: 'theme.disabled.background',
  },
  notCompletedIconDark: {
    color: 'theme.disabled.text',
  },
})
